import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export interface ProviderDraftData {
  provider_type: string;
  business_name: string;
  phone: string;
  email: string;
  whatsapp?: string;
  description?: string;
  photos: string[];
  services: string[];
  languages: string[];
  latitude: number;
  longitude: number;
  address: string;
  city?: string;
  state?: string;
  country?: string;
  schedule: any;
  credential_document_url?: string;
  registration_number?: string;
  id_document_front_url?: string;
  id_document_back_url?: string;
}

export const useProviderDraft = () => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const saveDraft = async (draftData: ProviderDraftData) => {
    setIsSaving(true);
    try {
      console.log('Saving draft...', draftData);

      const { data, error } = await supabase.functions.invoke('save-provider-draft', {
        body: draftData
      });

      if (error) throw error;

      toast.success(t('services.registerProvider.draftSaved'));
      return { success: true, provider_id: data.provider_id };
    } catch (err: any) {
      console.error('Error saving draft:', err);
      toast.error(t('services.registerProvider.draftError'));
      return { success: false, error: err.message };
    } finally {
      setIsSaving(false);
    }
  };

  const loadDraft = async (providerType: string) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('service_providers')
        .select('*')
        .eq('user_id', user.id)
        .eq('provider_type', providerType as any)
        .is('deleted_at', null)
        .maybeSingle();

      if (error) throw error;

      if (data && (data.provider_payment_status === 'draft' || data.provider_payment_status === 'pending')) {
        console.log('Draft loaded:', data);
        return { success: true, draft: data };
      }

      return { success: false, draft: null };
    } catch (err: any) {
      console.error('Error loading draft:', err);
      return { success: false, draft: null, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };

  const hasDraft = async (providerType: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data } = await supabase
        .from('service_providers')
        .select('id, provider_payment_status')
        .eq('user_id', user.id)
        .eq('provider_type', providerType as any)
        .is('deleted_at', null)
        .maybeSingle();

      return data && (data.provider_payment_status === 'draft' || data.provider_payment_status === 'pending');
    } catch (err) {
      console.error('Error checking draft:', err);
      return false;
    }
  };

  return {
    saveDraft,
    loadDraft,
    hasDraft,
    isSaving,
    isLoading
  };
};
